import { Sequence, SequenceItem } from '@repo/sdk/models/components';

import { SequenceWithValues } from '@/features/equipment-browser/equipment-details/state/sequencer-editor.state';

export const UNAVAILABLE = 0;

// Parse the dot-separated path to access nested values
export const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

/**
 * Removes the last stage (column) from all rows in a sequence.
 *
 * This function creates a new sequence with the last stage value removed from each equipment row.
 * The original sequence is not modified (immutable operation).
 *
 * @param sequence - The sequence to remove the last stage from
 * @returns A new sequence with the last stage removed from all rows
 *
 * @example
 * const sequence = {
 *   table: [
 *     { name: 'fan_1', stageValues: [0, 1, 2, 1] },
 *     { name: 'fan_2', stageValues: [0, 2, 1, 2] }
 *   ]
 * };
 * const result = removeStageFromSequence(sequence);
 * // result.table[0].stageValues => [0, 1, 2]
 * // result.table[1].stageValues => [0, 2, 1]
 */
export const removeStageFromSequence = (sequence: Sequence): Sequence => {
  return {
    table:
      sequence.table.map((sequenceItem: SequenceItem): SequenceItem => {
        return { ...sequenceItem, stageValues: sequenceItem.stageValues.slice(0, -1) };
      }) || [],
  };
};

/**
 * Removes the last stage from a SequenceWithValues object.
 *
 * This is a convenience wrapper around {@link removeStageFromSequence} that preserves
 * the metadata (key, label, etc.) while updating the sequence value.
 *
 * @param sequence - The SequenceWithValues object to remove the last stage from
 * @returns A new SequenceWithValues with the last stage removed
 *
 * @see {@link removeStageFromSequence}
 */
export const removeStageFromSequenceWithValues = (
  sequence: SequenceWithValues
): SequenceWithValues => {
  return {
    ...sequence,
    value: removeStageFromSequence(sequence.value as Sequence),
  };
};

/**
 * Adds a new stage (column) to all rows in a sequence.
 *
 * This function creates a new sequence with a new stage added to each equipment row.
 * The value added depends on whether the row is currently disabled:
 * - If the row is disabled (all values are UNAVAILABLE), adds UNAVAILABLE to maintain disabled state
 * - If the row is active, adds the specified default value
 *
 * This behavior ensures that disabled equipment remains disabled when adding new stages.
 *
 * @param sequence - The sequence to add a stage to
 * @param defaultValue - The value to use for active rows (typically OFF=1 or ON=2)
 * @returns A new sequence with an additional stage added to all rows
 *
 * @example
 * const sequence = {
 *   table: [
 *     { name: 'fan_1', stageValues: [0, 1, 2] },    // Active row
 *     { name: 'fan_2', stageValues: [0, 0, 0] }     // Disabled row
 *   ]
 * };
 * const result = addStageToSequence(sequence, 1);
 * // result.table[0].stageValues => [0, 1, 2, 1]  (adds defaultValue)
 * // result.table[1].stageValues => [0, 0, 0, 0]  (stays disabled with UNAVAILABLE)
 */
export const addStageToSequence = (sequence: Sequence, defaultValue: number): Sequence => {
  return {
    table:
      sequence.table.map((sequenceItem: SequenceItem): SequenceItem => {
        // Check if the row is disabled (all zeros)
        const isRowDisabled = sequenceItem.stageValues.every(
          (stageValue: number) => stageValue === UNAVAILABLE
        );
        // If disabled, add 0, otherwise add the provided default value
        const newStageValue = isRowDisabled ? UNAVAILABLE : defaultValue;
        return {
          ...sequenceItem,
          stageValues: [...sequenceItem.stageValues, newStageValue],
        };
      }) || [],
  };
};

/**
 * Adds a new stage to a SequenceWithValues object.
 *
 * This is a convenience wrapper around {@link addStageToSequence} that preserves
 * the metadata (key, label, etc.) while updating the sequence value.
 *
 * @param sequence - The SequenceWithValues object to add a stage to
 * @param defaultValue - The value to use for active rows
 * @returns A new SequenceWithValues with an additional stage
 *
 * @see {@link addStageToSequence}
 */
export const addStageToSequenceWithValues = (
  sequence: SequenceWithValues,
  defaultValue: number
): SequenceWithValues => {
  return {
    ...sequence,
    value: addStageToSequence(sequence.value as Sequence, defaultValue),
  };
};

/**
 * Disables a specific equipment row by setting all its stage values to UNAVAILABLE.
 *
 * This function creates a new sequence where the specified row (equipment) is disabled
 * by setting all of its stage values to UNAVAILABLE (0). The equipment name and other
 * metadata are preserved, and all other rows remain unchanged.
 *
 * Note: This doesn't actually "remove" the row from the table, it just disables it.
 *
 * @param sequence - The sequence to modify
 * @param rowIndex - The zero-based index of the row to disable
 * @returns A new sequence with the specified row disabled
 *
 * @example
 * const sequence = {
 *   table: [
 *     { name: 'fan_1', stageValues: [0, 1, 2, 1] },
 *     { name: 'fan_2', stageValues: [0, 2, 1, 2] }
 *   ]
 * };
 * const result = removeRowFromSequence(sequence, 0);
 * // result.table[0].stageValues => [0, 0, 0, 0]  (all UNAVAILABLE - disabled)
 * // result.table[1].stageValues => [0, 2, 1, 2]  (unchanged)
 */
export const removeRowFromSequence = (sequence: Sequence, rowIndex: number): Sequence => {
  return {
    table: sequence.table.map((sequenceItem: SequenceItem, index: number): SequenceItem => {
      if (index === rowIndex) {
        return {
          ...sequenceItem,
          stageValues: sequenceItem.stageValues.map(() => UNAVAILABLE),
        };
      }
      return sequenceItem;
    }),
  };
};

/**
 * Disables a specific equipment row in a SequenceWithValues object.
 *
 * This is a convenience wrapper around {@link removeRowFromSequence} that preserves
 * the metadata while disabling the specified row.
 *
 * @param sequence - The SequenceWithValues object to modify
 * @param rowIndex - The zero-based index of the row to disable
 * @returns A new SequenceWithValues with the specified row disabled
 *
 * @see {@link removeRowFromSequence}
 */
export const removeRowFromSequenceWithValues = (
  sequence: SequenceWithValues,
  rowIndex: number
): SequenceWithValues => {
  return {
    ...sequence,
    value: removeRowFromSequence(sequence.value as Sequence, rowIndex),
  };
};

/**
 * Enables a specific equipment row by setting all its stage values to the specified default value.
 *
 * This function creates a new sequence where the specified row (equipment) is enabled
 * by setting all of its stage values to the provided default value. This is typically used
 * to activate previously disabled equipment with a default state (e.g., OFF=1).
 *
 * The equipment name and other metadata are preserved, and all other rows remain unchanged.
 *
 * @param sequence - The sequence to modify
 * @param rowIndex - The zero-based index of the row to enable
 * @param defaultValue - The value to set for all stages (typically OFF=1 or another default state)
 * @returns A new sequence with the specified row enabled with the default value
 *
 * @example
 * const sequence = {
 *   table: [
 *     { name: 'fan_1', stageValues: [0, 0, 0, 0] },  // Disabled (all UNAVAILABLE)
 *     { name: 'fan_2', stageValues: [0, 2, 1, 2] }
 *   ]
 * };
 * const result = enableRowInSequence(sequence, 0, 1);  // Enable with OFF=1
 * // result.table[0].stageValues => [1, 1, 1, 1]  (enabled with defaultValue)
 * // result.table[1].stageValues => [0, 2, 1, 2]  (unchanged)
 */
export const enableRowInSequence = (
  sequence: Sequence,
  rowIndex: number,
  defaultValue: number
): Sequence => {
  return {
    table: sequence.table.map((sequenceItem: SequenceItem, index: number): SequenceItem => {
      if (index === rowIndex) {
        return {
          ...sequenceItem,
          stageValues: sequenceItem.stageValues.map(() => defaultValue),
        };
      }
      return sequenceItem;
    }),
  };
};

/**
 * Enables a specific equipment row in a SequenceWithValues object.
 *
 * This is a convenience wrapper around {@link enableRowInSequence} that preserves
 * the metadata while enabling the specified row with the provided default value.
 *
 * @param sequence - The SequenceWithValues object to modify
 * @param rowIndex - The zero-based index of the row to enable
 * @param defaultValue - The value to set for all stages in the enabled row
 * @returns A new SequenceWithValues with the specified row enabled
 *
 * @see {@link enableRowInSequence}
 */
export const enableRowInSequenceWithValues = (
  sequence: SequenceWithValues,
  rowIndex: number,
  defaultValue: number
): SequenceWithValues => {
  return {
    ...sequence,
    value: enableRowInSequence(sequence.value as Sequence, rowIndex, defaultValue),
  };
};

/**
 * Cycles the value of a specific cell in the sequence by applying a transformation function.
 *
 * This function allows you to change a single stage value in a specific equipment row
 * by applying a custom cycling function. The cycling function receives the current value
 * and returns the new value (e.g., OFF -> ON -> OFF for a toggle).
 *
 * All other cells in the sequence remain unchanged.
 *
 * @param sequence - The sequence to modify
 * @param rowIndex - The zero-based index of the equipment row
 * @param columnIndex - The zero-based index of the stage column
 * @param cycleFn - Function that transforms the current value to the next value
 * @returns A new sequence with the specified cell value updated
 *
 * @example
 * // Example: Toggle between OFF (1) and ON (2)
 * const toggleFn = (value: number) => value === 1 ? 2 : 1;
 * const sequence = {
 *   table: [
 *     { name: 'fan_1', stageValues: [0, 1, 2, 1] }
 *   ]
 * };
 * const result = cycleStageInSequence(sequence, 0, 2, toggleFn);
 * // result.table[0].stageValues => [0, 1, 1, 1]  (position [0,2] toggled from 2 to 1)
 */
export const cycleStageInSequence = (
  sequence: Sequence,
  rowIndex: number,
  columnIndex: number,
  cycleFn: (stageValue: number) => number
): Sequence => {
  return {
    table: sequence.table.map((sequenceItem: SequenceItem, index: number): SequenceItem => {
      if (index === rowIndex) {
        return {
          ...sequenceItem,
          stageValues: sequenceItem.stageValues.map(
            (stageValue: number, stageIndex: number): number => {
              if (stageIndex === columnIndex) {
                return cycleFn(stageValue);
              }
              return stageValue;
            }
          ),
        };
      }
      return sequenceItem;
    }),
  };
};

/**
 * Cycles the value of a specific cell in a SequenceWithValues object.
 *
 * This is a convenience wrapper around {@link cycleStageInSequence} that preserves
 * the metadata while updating the specified cell value.
 *
 * @param sequence - The SequenceWithValues object to modify
 * @param rowIndex - The zero-based index of the equipment row
 * @param columnIndex - The zero-based index of the stage column
 * @param cycleFn - Function that transforms the current value to the next value
 * @returns A new SequenceWithValues with the specified cell value updated
 *
 * @see {@link cycleStageInSequence}
 */
export const cycleStageInSequenceWithValues = (
  sequence: SequenceWithValues,
  rowIndex: number,
  columnIndex: number,
  cycleFn: (stageValue: number) => number
): SequenceWithValues => {
  return {
    ...sequence,
    value: cycleStageInSequence(sequence.value as Sequence, rowIndex, columnIndex, cycleFn),
  };
};
